<template>
    <div class="chart-card">
        <h5>ACCUMULATED CAPITAL GAIN V.S. TENURE PERIOD</h5>
        <div class="scatter-chart-wrap">
            <canvas ref="canvas"></canvas>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js';

export default {
    name: 'ScatterChart',
    props: {
        series: {
            type: Array,
            default: function() { return []; }
        }
    },
    data: function() {
        return {
            chartInstance: null
        };
    },
    computed: {
        chartData: function() {
            return this.normalizeGainSeries(this.series, []);
        }
    },
    watch: {
        series: function() {
            this.updateChart();
        }
    },
    mounted: function() {
        this.initChart();
    },
    beforeDestroy: function() {
        if(this.chartInstance) {
            this.chartInstance.destroy();
            this.chartInstance = null;
        }
    },
    methods: {
        initChart: function() {
            var ctx = this.$refs.canvas.getContext('2d');
            var points = this.chartData;
            var trendPoints = this.computeTrendLine(points);

            this.chartInstance = new Chart(ctx, {
                type: 'scatter',
                data: {
                    datasets: [
                        {
                            label: 'Capital Gain',
                            data: points,
                            backgroundColor: 'rgba(215, 182, 105, 0.84)',
                            pointRadius: 5,
                            pointHoverRadius: 7
                        },
                        {
                            label: 'Trend',
                            data: trendPoints,
                            type: 'line',
                            borderColor: 'rgba(211, 180, 107, 0.85)',
                            borderWidth: 2,
                            pointRadius: 0,
                            fill: false,
                            lineTension: 0,
                            showLine: true
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: { duration: 400 },
                    legend: { display: false },
                    tooltips: {
                        callbacks: {
                            title: function(tooltipItems, data) {
                                if(!tooltipItems || !tooltipItems.length) {
                                    return '';
                                }

                                var tooltipItem = tooltipItems[0];
                                var point = null;
                                if(data && data.datasets && data.datasets[tooltipItem.datasetIndex]) {
                                    point = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || null;
                                }

                                var propValue = point && point.prop !== undefined && point.prop !== null
                                    ? point.prop
                                    : null;
                                if(!propValue || typeof propValue !== 'object') {
                                    return 'Property';
                                }

                                var city = propValue.city || '';
                                var state = propValue.state || '';
                                var postcode = propValue.postcode || '';
                                var title = [city, state, postcode].filter(function(v) { return !!v; }).join(', ');
                                return title || 'Property';
                            },
                            label: function(tooltipItem, data) {
                                var point = null;
                                if(data && data.datasets && data.datasets[tooltipItem.datasetIndex]) {
                                    point = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || null;
                                }

                                var propValue = point && point.prop && typeof point.prop === 'object'
                                    ? point.prop
                                    : null;

                                if(!propValue) {
                                    return [
                                        'Tenure: ' + tooltipItem.xLabel + ' yrs'
                                    ];
                                }

                                var formatMoney = function(valueInCents) {
                                    var dollars = Number(valueInCents) / 100;
                                    if(!Number.isFinite(dollars)) {
                                        return '$0';
                                    }
                                    return '$' + Math.round(dollars).toLocaleString('en-AU', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0
                                    });
                                };

                                var currentValue = propValue.soldPrice || propValue.valuation;
                                return [
                                    'Purchase Price: ' + formatMoney(propValue.purchasePrice),
                                    'Current Value: ' + formatMoney(currentValue),
                                    'Capital Gain: ' + formatMoney(propValue.capitalGain),
                                    'Capital Annual Growth: ' + (Number.isFinite(Number(propValue.annualGrowth)) ? Number(propValue.annualGrowth) : 0) + '%',
                                    'Tenure: ' + tooltipItem.xLabel + ' yrs'
                                ];
                            }
                        }
                    },
                    scales: {
                        xAxes: [{
                            type: 'linear',
                            scaleLabel: {
                                display: true,
                                labelString: 'Holding Period (years)',
                                fontColor: '#3a6056',
                                fontSize: 11
                            },
                            ticks: { fontColor: '#4a7068' },
                            gridLines: { color: 'rgba(47, 122, 108, 0.15)' }
                        }],
                        yAxes: [{
                            type: 'linear',
                            scaleLabel: {
                                display: true,
                                labelString: 'Gross Captial Growth(%)',
                                fontColor: '#3a6056',
                                fontSize: 11
                            },
                            ticks: { fontColor: '#4a7068' },
                            gridLines: { color: 'rgba(47, 122, 108, 0.15)' }
                        }]
                    }
                }
            });
        },
        updateChart: function() {
            if(!this.chartInstance) return;
            var points = this.chartData;
            this.chartInstance.data.datasets[0].data = points;
            this.chartInstance.data.datasets[1].data = this.computeTrendLine(points);
            this.chartInstance.update();
        },
        computeTrendLine: function(points) {
            if(!Array.isArray(points) || !points.length) return [];

            var xValues = points.map(function(point) { return point.x; });
            var yValues = points.map(function(point) { return point.y; });

            var minX = Math.min.apply(null, xValues);
            var maxX = Math.max.apply(null, xValues);
            var minY = Math.min.apply(null, yValues);
            var maxY = Math.max.apply(null, yValues);

            if(!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
                return [];
            }

            if(minX === maxX && minY === maxY) {
                return [
                    { x: minX, y: minY },
                    { x: maxX, y: maxY }
                ];
            }

            // Use the chart's data bounds to draw a centered 45deg-style diagonal reference line.
            return [
                { x: minX, y: minY },
                { x: maxX, y: maxY }
            ];
        },
        toNumber: function(value, fallback) {
            if(fallback === undefined) fallback = 0;
            if(typeof value === 'number' && Number.isFinite(value)) {
                return value;
            }
            if(typeof value === 'string') {
                var parsed = Number(value.replace(/[^\d.-]/g, ''));
                return Number.isFinite(parsed) ? parsed : fallback;
            }
            return fallback;
        },
        normalizeGainSeries: function(source, fallback) {
            if(fallback === undefined) fallback = [];
            var self = this;
            var list = Array.isArray(source)
                ? source
                : (source && typeof source === 'object'
                    ? Object.entries(source).map(function(entry) { return { tenure: entry[0], gain: entry[1] }; })
                    : []);

            var normalized = list
                .map(function(item, index) {
                    if(typeof item === 'number' || typeof item === 'string') {
                        return { x: index + 1, y: self.toNumber(item, NaN), prop: '' };
                    }
                    if(item && typeof item === 'object') {
                        var xVal = item.tenureMonths !== undefined && item.tenureMonths !== null
                            ? item.tenureMonths
                            : (item.x !== undefined && item.x !== null ? item.x : index + 1);
                        var yVal = item.accumulatedCapitalGain !== undefined && item.accumulatedCapitalGain !== null
                            ? item.accumulatedCapitalGain
                            : item.y;
                        return {
                            x: self.toNumber(xVal, index + 1),
                            y: self.toNumber(yVal, NaN),
                            prop: item.prop
                        };
                    }
                    return { x: index + 1, y: NaN, prop: '' };
                })
                .filter(function(point) { return Number.isFinite(point.x) && Number.isFinite(point.y); });

            if(normalized.length) {
                var filtered = this.filterOutliers(normalized);
                return filtered.length ? filtered : normalized;
            }

            if(Array.isArray(fallback)) {
                var fallbackPoints = fallback
                    .map(function(value, index) { return { x: index + 1, y: self.toNumber(value, NaN), prop: '' }; })
                    .filter(function(point) { return Number.isFinite(point.y); });

                if(fallbackPoints.length) {
                    var filteredFallback = this.filterOutliers(fallbackPoints);
                    return filteredFallback.length ? filteredFallback : fallbackPoints;
                }

                return fallbackPoints;
            }

            return [];
        },
        filterOutliers: function(points) {
            if(!Array.isArray(points) || points.length < 6) {
                return points || [];
            }

            var sortedByY = points.slice().sort(function(a, b) { return a.y - b.y; });
            var q1 = this.quantile(sortedByY, 0.25);
            var q3 = this.quantile(sortedByY, 0.75);
            var iqr = q3 - q1;

            if(!Number.isFinite(q1) || !Number.isFinite(q3) || !Number.isFinite(iqr) || iqr <= 0) {
                return points;
            }

            var lower = q1 - (1.5 * iqr);
            var upper = q3 + (1.5 * iqr);
            var filtered = points.filter(function(point) {
                return point.y >= lower && point.y <= upper;
            });

            return filtered.length >= Math.max(4, Math.ceil(points.length * 0.6)) ? filtered : points;
        },
        quantile: function(sortedPoints, fraction) {
            if(!Array.isArray(sortedPoints) || !sortedPoints.length) {
                return NaN;
            }

            var index = (sortedPoints.length - 1) * fraction;
            var lower = Math.floor(index);
            var upper = Math.ceil(index);

            if(lower === upper) {
                return sortedPoints[lower].y;
            }

            var weight = index - lower;
            return sortedPoints[lower].y + ((sortedPoints[upper].y - sortedPoints[lower].y) * weight);
        }
    }
};
</script>

<style scoped>
.chart-card {
    margin-top: 0;
    border-radius: 0;
    border: none;
    background: transparent;
    padding: 0;
}

.chart-card h5 {
    margin: 0 0 10px;
    text-align: center;
    letter-spacing: 0.08em;
    font-size: 0.88rem;
    font-weight: 500;
}

.scatter-chart-wrap {
    position: relative;
    height: 520px;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
    padding: 12px;
    box-sizing: border-box;
}

.scatter-chart-wrap canvas {
    width: 100% !important;
    height: 100% !important;
}

@media (max-width: 768px) {
    .scatter-chart-wrap {
        height: 300px;
    }
}
</style>
